// Nishi-Shinjuku east (x128-255, y96-143): colder corporate fronts, a parking deck, a service wall and a glass roof.
// Kind names are prefixed "nishiEast" (global registry).
import { K, px, dither, dk, alpha, speckle } from './hd-kit';
import { registerFront, registerRoof } from './hd-registry';
import { display, door, vending, plant, bandSign, bladeSign, fitS } from './hd-parts';
import { hash2 } from '../../common/src/util';

// ---------------------------------------------------------------- roof: dark glass with panel grid
registerRoof('nishiEastGlass', { paint(g, x, y, w, h, seed) {
  px(g, x, y, w, h, dk(K.glass, 0.45));
  for (let i = 0; i < w; i += 24) px(g, x + i, y, 1, h, K.metalLo);
  for (let j = 0; j < h; j += 24) px(g, x, y + j, w, 1, K.metalLo);
  for (let j = 0; j < h; j += 24) for (let i = 0; i < w; i += 24) if (hash2(x + i + seed, y + j) < 0.3) px(g, x + i + 1, y + j + 1, 23, 23, alpha(K.glassHi, 0.25));
  for (let k = 0; k < w + h; k += 18) px(g, x + Math.min(k, w - 1), y + Math.max(0, k - w), 2, 1, alpha(K.white, 0.3));
} });

// ---------------------------------------------------------------- fronts
// glass office tower: lobby with a cold canopy and a blade sign; the upper wall is a curtain wall
registerFront('nishiEastCorp', { wall: dk(K.cyan, 0.82), upper: 'curtain' }, ({ g, x, y, w, h, gf, gy, sign, L }) => {
  if (sign) { if (h < 150) bandSign(g, x + w / 2, y + 8, sign.text, sign.color, L, fitS(sign.text, w)); else bladeSign(g, x + 6, y + 10, sign.text, sign.color, L, 2); }
  px(g, x + 10, gy - 8, w - 20, 5, K.ink); px(g, x + 10, gy - 8, w - 20, 2, K.concreteHi); px(g, x + 10, gy - 3, w - 20, 3, alpha(K.night, 0.45));
  px(g, x + 12, gy - 3, w - 24, 1, K.cyan); L(x + 12, gy - 3, w - 24, 3, K.cyan, 0.4, true);
  const dw = 44;
  door(g, x + w / 2 - dw / 2, gy + 4, dw, gf - 6, K.cyan, L);
  for (let sx = x + 14; sx + 40 < x + w / 2 - dw / 2 - 4; sx += 50) display(g, sx, gy + 8, 40, gf - 14, dk(K.cyan, 0.3), L, false);
  for (let sx = x + w / 2 + dw / 2 + 10; sx + 40 < x + w - 8; sx += 50) display(g, sx, gy + 8, 40, gf - 14, dk(K.cyan, 0.3), L, false);
  plant(g, x + w / 2 - dw / 2 - 12, gy + gf - 20);
});

// hotel: pink-white blade sign, drive-in canopy with a warm door
registerFront('nishiEastHotel', { wall: dk(K.cyan, 0.7), wallAc: true }, ({ g, x, y, w, h, gf, gy, sign, L }) => {
  if (sign) bladeSign(g, x + w - 30, y + 8, sign.text, sign.color, L, 3);
  const cx = x + w / 2 - 50;
  px(g, cx, gy - 5, 100, 7, K.ink); px(g, cx + 1, gy - 4, 98, 3, K.concreteHi); px(g, cx + 1, gy, 98, 2, alpha(K.night, 0.5)); L(cx, gy - 4, 100, 6, K.amber, 0.4, true);
  door(g, cx + 30, gy + 6, 40, gf - 8, K.amber, L);
  display(g, cx - 40, gy + 10, 34, gf - 18, K.amber, L, false); display(g, cx + 106, gy + 10, 34, gf - 18, K.amber, L, false);
  plant(g, cx + 8, gy + gf - 20); plant(g, cx + 80, gy + gf - 20);
});

// parking deck: open slabs with parked car shapes and a ramp mouth
registerFront('nishiEastDeck', { wall: K.concreteLo, upper: 'none' }, ({ g, x, y, w, h, gf, gy, sign, L }) => {
  const levels = Math.max(2, Math.floor((h - 8) / 28));
  for (let k = 0; k < levels; k++) {
    const ly = y + 6 + k * Math.floor((h - 12) / levels), lh = Math.floor((h - 12) / levels) - 4;
    px(g, x + 4, ly, w - 8, lh, K.ink); px(g, x + 4, ly + lh - 2, w - 8, 2, K.concreteHi);
    for (let cx = x + 12; cx + 22 < x + w - 8; cx += 30) {
      const v = hash2(cx, ly);
      if (v < 0.6) { const col = v < 0.2 ? K.cyan : v < 0.4 ? K.metal : K.pink; px(g, cx, ly + lh - 12, 22, 9, dk(col, 0.45)); px(g, cx + 4, ly + lh - 16, 13, 5, dk(col, 0.6)); px(g, cx + 2, ly + lh - 5, 18, 2, K.ink); }
    }
    dither(g, x + 4, ly, w - 8, 6, alpha(K.amber, 0.18)); L(x + 8, ly + 2, w - 16, 4, K.amber, 0.14);
  }
  if (sign) bandSign(g, x + w / 2, gy + 2, sign.text, sign.color, L, fitS(sign.text, w));
  px(g, x + 4, y + h - 4, w - 8, 3, K.warmHi); // yellow kerb stripe
});

// service wall: shutters, hazard stripe, pipes and vents on the south edge of the map
registerFront('nishiEastService', { wall: K.wallLo, upper: 'none' }, ({ g, x, y, w, h, gf, gy, L }) => {
  px(g, x, y + h - 14, w, 3, K.warmHi); for (let i = 0; i < w; i += 12) px(g, x + i, y + h - 14, 6, 3, K.ink);
  for (let i = x + 20; i + 70 < x + w; i += 120) {
    px(g, i - 2, y + 10, 74, h - 30, K.ink);
    for (let j = 0; j < h - 38; j += 3) { px(g, i, y + 12 + j, 70, 2, K.metal); px(g, i, y + 14 + j, 70, 1, K.metalLo); }
    px(g, i + 22, y + h - 24, 26, 3, alpha(K.cyan, 0.5)); L(i + 22, y + h - 25, 26, 4, K.cyan, 0.25, true);
  }
  for (let i = x + 100; i + 16 < x + w; i += 120) { px(g, i, y + 8, 14, h - 24, K.ink); for (let j = 0; j < h - 30; j += 4) px(g, i + 1, y + 10 + j, 12, 1, K.metalHi); }
  speckle(g, x, y, w, h, K.concreteLo, 0.03, 7);
});

// street-level corner shop: cold glass with a pink and a cyan neon strip
registerFront('nishiEastKiosk', { wall: K.plasterLo }, ({ g, x, y, w, h, gf, gy, sign, L }) => {
  if (sign) bandSign(g, x + w / 2, y + 13, sign.text, sign.color, L, fitS(sign.text, w));
  px(g, x + 6, gy - 7, w - 12, 2, K.pink); px(g, x + 6, gy - 4, w - 12, 2, K.cyan); L(x + 6, gy - 8, w - 12, 6, K.pink, 0.3);
  display(g, x + 10, gy + 6, Math.min(110, w / 2 - 14), gf - 12, K.cyan, L);
  door(g, x + w / 2 + 4, gy + 4, 40, gf - 6, K.white, L);
  vending(g, x + w - 52, gy + gf - 40, dk(K.pink, 0.4), L); vending(g, x + w - 28, gy + gf - 40, dk(K.cyan, 0.45), L);
});
